import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import Table from "../../../core/common/dataTable/index";
import FeatherIcon from "feather-icons-react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import useApi from "../../../core/customhook/useApi";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import Swal from "sweetalert2";

const AttendanceTeam = () => {
  const [empname, setEmpname] = useState("");
  const [empdata, setEmpdata] = useState([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
  const [hoveredEvent, setHoveredEvent] = useState<any>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [calendarEvents, setCalendarEvents] = useState<any>([]);
  const [months, setMonths] = useState("")
  const [year, setYear] = useState("")

  const handleDateClick = (info: any) => {
    const curruntDate = dayjs().format("YYYY-MM-DD");
    if (info.dateStr >= curruntDate) {
      Swal.fire({
        title: "Not allowed",
        text: "You can't select today's or future dates.",
        icon: "info",
        confirmButtonText: "OK",
      });
      return;
    }
    const selectedDayjs = dayjs(info.date);
  };

  const {
    data: teamData,
    loading: teamLoading,
    error: teamError,
  } = useApi("/api/manager/team-member", {
    method: "GET",
    autoCall: true,
  });

  useEffect(() => {
    if (teamData?.success) {
      setEmpdata(teamData?.data);
    }
  }, [teamData]);

  const {
    data: attendanceData,
    loading: attendanceLoading,
    error: attendanceErrro,
    callApi
  } = useApi("/api/get-employee-attendance-details", {
    method: "POST",
    payload: {
      employee_id: searchValue,
      month: months,
      year: year
    },
    autoCall: false,
  });

  useEffect(() => {
    if (!year || !months) return;

    callApi();
  }, [year, months, searchValue]);

  useEffect(() => {
    if (!attendanceData?.success) return;

    // 1️⃣ Map leave_type_id to short code
    const leaveTypeShortMap: Record<number, string> = {
      1: "EL", // Earned Leave
      2: "SL", // Sick Leave
      3: "RH", // Restricted Holiday
      4: "ML", // Maternity Leave
      5: "LWP", // Leave Without Pay
      7: "BL", // Bereavement Leave
      8: "WL", // Wedding Leave
      9: "HL", // Happiness Leave
    };

    // 2️⃣ Map leave dates with description
    const leaveByDate: Record<
      string,
      { code: string; description?: string; isHalfDay: boolean }
    > = {};
    attendanceData.approvedLeaves?.forEach((leave: any) => {
      const from = dayjs(leave.from_date);
      const to = leave.to_date ? dayjs(leave.to_date) : from;

      for (let d = from; d.isSameOrBefore(to); d = d.add(1, "day")) {
        leaveByDate[d.format("YYYY-MM-DD")] = {
          code: leaveTypeShortMap[leave.leave_type_id] || "LEAVE",
          isHalfDay: leave.days === 0.5,
          description: leave.description,
        };
      }
    });

    // 3️⃣ Map OD dates with description
    const odByDate: Record<
      string,
      { code: string; description?: string; isHalfDay: boolean }
    > = {};
    attendanceData.approvedODs?.forEach((od: any) => {
      const from = dayjs(od.from_date);
      const to = od.to_date ? dayjs(od.to_date) : from;

      for (let d = from; d.isSameOrBefore(to); d = d.add(1, "day")) {
        odByDate[d.format("YYYY-MM-DD")] = {
          code: "OD",
          isHalfDay: od.days === 0.5,
          description: od.description,
        };
      }
    });

    // 4️⃣ Map attendance events with priority: Leave > OD > Attendance
    const attendanceEvents = attendanceData.data.map((record: any) => {
      const date = record.attendance_date;

      let displayStatus = record.status;
      let title = record.status;

      // Leave has priority
      if (leaveByDate[date]) {
        displayStatus = leaveByDate[date].code;
        title = leaveByDate[date].isHalfDay
          ? `${displayStatus} (${leaveByDate[date].description})`
          : displayStatus;
      }
      // OD second priority
      else if (odByDate[date]) {
        displayStatus = odByDate[date].code;
        title = odByDate[date].isHalfDay
          ? `${displayStatus} (${odByDate[date].description})`
          : displayStatus;
      }

      // 5️⃣ Determine color for the dot
      let color =
        displayStatus === "P" ? "#28a745" : // Present
          displayStatus === "A" ? "#dc3545" : // Absent
            displayStatus === "HP" ? "#ffc107" : // Half Pay
              displayStatus === "WO" ? "#007bff" : // Work Off
                displayStatus === "SH" ? "#fd7e14" : // Special Holiday
                  displayStatus === "EL" ? "#17a2b8" : // Earned Leave
                    displayStatus === "SL" ? "#6f42c1" : // Sick Leave
                      displayStatus === "RH" ? "#20c997" : // Restricted Holiday
                        displayStatus === "ML" ? "#fd7e14" : // Maternity Leave
                          displayStatus === "LWP" ? "#dc3545" : // Leave Without Pay (red)
                            displayStatus === "BL" ? "#6610f2" : // Bereavement Leave
                              displayStatus === "WL" ? "#ffc107" : // Wedding Leave
                                displayStatus === "HL" ? "#007bff" : // Happiness Leave
                                  displayStatus === "OD" ? "#6610f2" : // On Duty
                                    "#6c757d";                     // Default gray

      let inTime = record.in_time_machine;
      let outTime = record.out_time_machine;

      if (
        record.regularisation_status === "APPROVED" &&
        record.in_time_machine === "00:00:00" &&
        record.out_time_machine === "00:00:00"
      ) {
        inTime = record.self_regn_in_time || "-";
        outTime = record.self_regn_out_time || "-";
      }

      return {
        title,
        date,
        color,
        extendedProps: {
          attendance_log_id: record.attendance_log_id,
          status: record.status,
          leaveCode: leaveByDate[date]?.code || null,
          isHalfDay: leaveByDate[date]?.isHalfDay || odByDate[date]?.isHalfDay || false,
          leaveDescription: leaveByDate[date]?.description || null,
          odCode: odByDate[date]?.code || null,
          odDescription: odByDate[date]?.description || null,
          in_time: inTime,
          out_time: outTime,
        },
      };
    });

    // 6️⃣ Map holidays
    const holidayEvents = attendanceData.holidays?.map((holiday: any) => {
      const isRH = holiday.holiday_category === "RH";
      return {
        title: holiday.holiday,
        date: holiday.holiday_date,
        backgroundColor: "transparent",
        extendedProps: {
          isHoliday: true,
          category: holiday.holiday_category,
          textColor: isRH ? "#007bff" : "#dc3545",
        },
      };
    }) || [];

    // 7️⃣ Combine all events
    const allEvents = [...attendanceEvents, ...holidayEvents];
    setCalendarEvents(allEvents);
  }, [attendanceData]);

  const empcolumns = [
    {
      title: "EmpCode",
      dataIndex: "employee_id",
      render: (text: string, record: any) => (
        <Link
          to="#"
          onClick={() => {
            setSearchValue(record.employee_id);
            setEmpname(record.employee_name);
            setModalOpen(false);
          }}
          style={{ textDecoration: "underline", cursor: "pointer" }}
          data-bs-dismiss="modal"
        >
          {text}
        </Link>
      ),
      sorter: (a: any, b: any) => a.employee_id.localeCompare(b.employee_id),
    },
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      render: (text: string, record: any) => (
        <Link
          to="#"
          onClick={() => {
            setSearchValue(record.employee_id);
            setEmpname(record.employee_name);
            setModalOpen(false);
          }}
          style={{ textDecoration: "underline", cursor: "pointer" }}
          data-bs-dismiss="modal"
        >
          {text}
        </Link>
      ),
      sorter: (a: any, b: any) =>
        a.employee_name.localeCompare(b.employee_name),
    },
    {
      title: "Designation",
      dataIndex: "designation", // Change From to designation
      sorter: (a: any, b: any) => a.designation.localeCompare(b.designation),
    },
    {
      title: "Department",
      dataIndex: "department", // Change To to department
      sorter: (a: any, b: any) => a.department.localeCompare(b.department),
    },
    {
      title: "Locatation",
      dataIndex: "branch_name", // Change To to branch_name
      sorter: (a: any, b: any) => a.branch_name.localeCompare(b.branch_name),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Team Attendance</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.employeeDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Attendance</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    View Team Attendance List List
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div style={{ position: "relative", width: "500px" }}>
                <input
                  type="text"
                  name="empname"
                  id="employeeSearch"
                  className="form-control pe-5"
                  placeholder="Type name / code"
                  style={{ width: "100%" }}
                  value={empname}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setEmpname(value);
                    setSearchValue("");

                    if (value.trim()) {
                      const matches = empdata.filter((item: any) =>
                        item.employee_name
                          .toLowerCase()
                          .includes(value.toLowerCase())
                      );
                      setFilteredSuggestions(matches);
                      setShowSuggestions(true);
                    } else {
                      setFilteredSuggestions([]);
                      setShowSuggestions(false);
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "8px",
                    transform: "translateY(-50%)",
                    padding: "4px 8px",
                  }}
                  onClick={() => setModalOpen(true)}
                  data-bs-toggle="modal"
                  data-bs-target="#bs-example-modal-lg"
                >
                  <FeatherIcon icon="more-horizontal" size="16" />
                </button>
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <ul
                    className="dropdown-menu show"
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      overflowY: "auto",
                      position: "absolute",
                      zIndex: 1000,
                    }}
                  >
                    {filteredSuggestions.map((item, index) => (
                      <li
                        key={index}
                        className="dropdown-item"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSearchValue(item.employee_id);
                          setEmpname(item.employee_name);
                          setShowSuggestions(false);
                        }}
                      >
                        {item.employee_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 col-xxl-12 col-xl-12  theiaStickySidebar">
              <div className="stickybar">
                <div className="card border-0">
                  <div className="card-body">
                    {/* <FullCalendar
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                      ]}
                      initialView="dayGridMonth"
                      headerToolbar={{
                        start: "",
                        center: "title",
                        end: "prev,next",
                      }}
                      height={500}
                      events={calendarEvents}
                    /> */}

                    <FullCalendar
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                      ]}
                      initialView="dayGridMonth"
                      headerToolbar={{
                        start: "",
                        center: "title",
                        end: "prev,next",
                      }}
                      height={500}
                      events={calendarEvents}
                      datesSet={(info) => {
                        const month = dayjs(info.view.currentStart).format("MM");
                        const year = dayjs(info.view.currentStart).format("YYYY");
                        setMonths(month)
                        setYear(year)
                      }}
                      // dateClick={(info) => handleDateClick(info)}
                      dayCellDidMount={(arg) => {
                        arg.el.style.cursor = "pointer";
                      }}
                      dayCellContent={({ date }) => {
                        const formattedDate = dayjs(date).format("YYYY-MM-DD");

                        // Find matching event
                        const matchedEvent = calendarEvents.find(
                          (e: any) => e.date === formattedDate
                        );

                        let dotColor = "";

                        if (matchedEvent) {
                          const { title, extendedProps } = matchedEvent;

                          if (extendedProps?.isHoliday) {
                            dotColor = extendedProps.category === "RH" ? "#007bff" : "#dc3545"; // blue or red
                          } else {
                            // Attendance status
                            switch (title) {
                              case "P":
                                dotColor = "#28a745";
                                break;
                              case "A":
                                dotColor = "#dc3545";
                                break;
                              case "HP":
                                dotColor = "#ffc107";

                                break;
                              case "WO":
                                dotColor = "#007bff";
                                break;
                              case "SH":
                                dotColor = "#fd7e14"; // yellow
                                break;
                              default:
                                dotColor = "#6c757d"; // grey for unknowns
                            }
                          }
                        }
                        return (
                          <div
                            style={{
                              position: "relative",
                              textAlign: "center",
                            }}
                          >
                            <div>{date.getDate()}</div>
                            {dotColor && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  marginTop: "5px",
                                }}
                              >
                                <span
                                  title={matchedEvent?.title}
                                  style={{
                                    width: 10,
                                    height: 10,
                                    backgroundColor: dotColor,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      }}
                      // eventContent={(eventInfo) => {
                      //   const status = eventInfo.event.extendedProps.status;
                      //   let color = "red";

                      //   if (status === "P") color = "#000"; // green
                      //   else if (status === "A") color = "#000"; // red
                      //   else if (status === "HP") color = "#000"; // orange

                      //   return (
                      //     <div
                      //       style={{
                      //         color: color, // font color only
                      //         fontWeight: "bold",
                      //         fontSize: "11px", // increased font size
                      //         textAlign: "center",
                      //         userSelect: "none",
                      //         whiteSpace: "nowrap",
                      //       }}
                      //       title={`Status: ${status}`}
                      //     >
                      //       {eventInfo.event.title}
                      //     </div>
                      //   );
                      // }}
                      eventContent={(eventInfo) => {
                        const status = eventInfo.event.extendedProps.status;
                        const isHoliday = eventInfo.event.extendedProps.isHoliday;
                        const textColor = eventInfo.event.extendedProps.textColor || "white";

                        let label = eventInfo.event.title;

                        return (
                          <div
                            style={{
                              color: textColor,
                              fontWeight: "bold",
                              fontSize: "11px",
                              textAlign: "center",
                              userSelect: "none",
                              whiteSpace: "nowrap",
                            }}
                            title={label}
                          >
                            {label}
                          </div>
                        );
                      }}
                      eventMouseEnter={(info) => {
                        const event = info.event.extendedProps;
                        setHoveredEvent({
                          date: info.event.startStr,
                          status: event.status,
                          in_time: event.in_time,
                          out_time: event.out_time,
                          x: info.jsEvent.pageX,
                          y: info.jsEvent.pageY,
                        });
                      }}

                      eventMouseLeave={() => {
                        setHoveredEvent(null);
                      }}

                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              <div className="card p-4 shadow-sm">
                <h4 className="mb-3">Status Legend</h4>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "35px",
                  }}
                >
                  {/* Present */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#28a745", // green
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Present</span>
                  </div>

                  {/* Absent */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#dc3545", // red
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Absent</span>
                  </div>

                  {/* Half Day */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#ffc107", // orange
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Half Day</span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#fd7e14", // yellow
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Short Hour</span>
                  </div>

                  {/* Weekend Off */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#007bff", // yellow (WO)
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Weekend Off</span>
                  </div>

                  {/* RH Holiday */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "transparent",
                        border: "2px solid #007bff", // blue border for RH
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Restricted Holiday (RH)</span>
                  </div>

                  {/* MH Holiday */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#dc3545", // red
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Mandatory Holiday (MH)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>


      {hoveredEvent && (
        <div
          style={{
            position: "absolute",
            top: hoveredEvent.y + 10,
            left: hoveredEvent.x + 10,
            background: "white",
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "8px 12px",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
            zIndex: 9999,
            fontSize: "13px",
            minWidth: "180px",
          }}
        >
          <strong>Date:</strong> {hoveredEvent.date} <br />
          <strong>Status:</strong> {hoveredEvent.status} <br />
          <strong>Punch In:</strong> {hoveredEvent.in_time || "—"} <br />
          <strong>Punch Out:</strong> {hoveredEvent.out_time || "—"}
        </div>
      )}

      <div
        className={`modal fade ${modalOpen ? "show" : ""}`}
        id="bs-example-modal-lg"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden={!modalOpen}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <img
                src="/assets/img/logo/weconnectlogo.png"
                alt=""
                style={{ height: "50px" }}
              />
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setModalOpen(false)}
              />
            </div>
            <div className="modal-body">
              <div className="card-body p-0">
                <Table
                  dataSource={empdata}
                  columns={empcolumns}
                  Selection={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AttendanceTeam;
